import type { GraphemeLessonPart } from "@-/dev.improve.haniverse.lib.level";
import { useCallback } from "react";
import { useMutation, useQueryClient } from "@-/com.npmjs.tanstack__react-query";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import useLatest from "@-/com.npmjs.use-latest";
import { ActorDatabaseContext } from "@-/dev.improve.haniverse.contexts.database.actor";
import { captureAnalyticsEvent } from "@-/dev.improve.haniverse.lib.analytics";
import {
  readReviewRecharge,
  recordReviewRechargeIntroduction,
  useDueRowsCountQueryOptions,
} from "@-/dev.improve.haniverse.lib.cards.review";
import { NavContext } from "@-/dev.improve.haniverse.routes.nav._context";
import {
  useCreateUserCard,
  useCreateUserPronunciationCards,
} from "@-/dev.improve.haniverse.routes.nav.app._lib.user-card.create";
import { useFreeTierGate } from "@-/dev.improve.haniverse.components.superwall";
import { useDailyNewGraphemeAllowanceQueryOptions } from "@-/dev.improve.haniverse.lib.learn.new-grapheme-limit";
import { useStudiedGraphemesQueryOptions } from "@-/dev.improve.haniverse.lib.cards.user-card.grapheme.meaning.read";
import { toast } from "@-/com.npmjs.sonner";

/**
 * Adds the lesson's new character to study, or presents the paywall when it is past the free tier
 * or a free learner has already introduced the free tier's daily allowance of new characters. A
 * learner who reached the lower limit they set themselves is told so, with a way to that setting.
 *
 * Gating here rather than at each of the three call sites (reveal press, "next card" button,
 * review completion) is what makes the boundary impossible to route around: every lesson-driven card
 * creation goes through this hook.
 *
 * Returns `false` when the add was blocked so callers can skip whatever they would have done next
 * (advancing the lesson, marking the session as having learned a character).
 *
 * A pronunciation-level lesson (`part: "pronunciation"`) enrolls only the authored readings. Its
 * example may preview a character whose meaning belongs to a later level, so it introduces no
 * meaning card and neither the daily allowance nor review recharge applies to it.
 *
 * The own-limit notice's Settings action calls `openDailyNewCardsSettings`. Navigation belongs to
 * the caller (the Learn deck owns its routes), so enrollment mounts no page navigator of its own.
 */
export function useCreateNewMorphemeLessonUserCard({
  openDailyNewCardsSettings,
}: {
  /** Opens Settings → Daily New Cards. Without it the own-limit notice offers no action. */
  openDailyNewCardsSettings?: () => void;
} = {}) {
  const createUserCardMutation = useCreateUserCard();
  const createUserPronunciationCardsMutation = useCreateUserPronunciationCards();
  const { canAddToStudy, openUpgradeToPremium } = useFreeTierGate("lesson_enroll");
  const [actorDb] = useContextSelectors(ActorDatabaseContext, (c) => c.db);
  const queryClient = useQueryClient();
  const dueRowsCountQueryOptionsRef = useLatest(useDueRowsCountQueryOptions());
  const dailyNewGraphemeAllowanceQueryOptions = useDailyNewGraphemeAllowanceQueryOptions();
  const studiedGraphemesQueryOptions = useStudiedGraphemesQueryOptions();
  const openDailyNewCardsSettingsRef = useLatest(openDailyNewCardsSettings);
  const [setHasLearnedNewCharacterThisSession, setHasLearnFlowStartedThisSession] =
    useContextSelectors(
      NavContext,
      (c) => c.setHasLearnedNewCharacterThisSession,
      (c) => c.setHasLearnFlowStartedThisSession,
    );

  // A shared `scope.id` makes TanStack run these mutations serially: the next card creation only
  // starts once the previous one's `mutationFn` has settled. Card creation is a read-modify-write
  // against the user's study state; without serialization two overlapping writes could interleave
  // and let a "next lesson" resolution that reads the studied set from the top
  // (`useNextLessonQueryOptions()` with no anchor — the review/summary/skip paths) see a half-updated
  // set. (The after-current paths anchor positionally on the current grapheme, so they don't depend
  // on the write landing — but the from-the-top paths still read the live studied set.)
  const createUserCardWithProgressMutation = useMutation({
    mutationFn: async ({
      grapheme,
      part,
      pinyin,
    }: {
      grapheme: string;
      part: GraphemeLessonPart | undefined;
      /** Single-reading 2-P1 slot (干 gan4, then 干 gan1). */
      pinyin?: string;
    }): Promise<boolean> => {
      if (!canAddToStudy(grapheme)) {
        // Past the free tier. Present the paywall and report the block — deliberately WITHOUT
        // setting the session progress flags, so the lesson does not advance past a character the
        // user never actually started studying.
        captureAnalyticsEvent("learn_blocked", { grapheme, reason: "free_tier" });
        openUpgradeToPremium();
        return false;
      }
      if (part === "pronunciation") {
        await createUserPronunciationCardsMutation.mutateAsync({
          graphemeCard: { front: grapheme },
          onlyPinyin: pinyin,
        });
        setHasLearnFlowStartedThisSession(true);
        setHasLearnedNewCharacterThisSession(true);
        return true;
      }
      // The lesson resolver stops offering characters once the day's allowance is spent, so this
      // only catches a lesson resolved before the limit was reached (another device, a stale
      // preview). An already-studied character re-enrolls nothing and is never blocked.
      const [allowance, studiedGraphemes] = await Promise.all([
        queryClient.fetchQuery(dailyNewGraphemeAllowanceQueryOptions),
        queryClient.fetchQuery(studiedGraphemesQueryOptions),
      ]);
      if (allowance.isLimitReached && !studiedGraphemes.has(grapheme)) {
        if (allowance.isFreeTierCap) {
          captureAnalyticsEvent("learn_blocked", { grapheme, reason: "daily_limit" });
          openUpgradeToPremium();
        } else {
          toast(
            `You've reached today's limit of ${allowance.limit.toLocaleString()} new cards`,
            openDailyNewCardsSettingsRef.current === undefined
              ? undefined
              : {
                  action: {
                    label: "Settings",
                    onClick: () => openDailyNewCardsSettingsRef.current?.(),
                  },
                },
          );
        }
        return false;
      }
      // Read BEFORE enrolling: the new card is itself due at once, and the pre-stored review
      // recharge balance counts from the latest introduction, which this enrollment moves.
      const readDueReviewCount = (): Promise<number> =>
        queryClient.fetchQuery(dueRowsCountQueryOptionsRef.current);
      const [reviewRechargeBefore, dueReviewCount] = await Promise.all([
        readReviewRecharge(actorDb(import.meta.location)),
        readDueReviewCount(),
      ]);
      const startedStudyingAt = new Date().toISOString();
      const meaningCard = await createUserCardMutation.mutateAsync({
        graphemeCard: { front: grapheme },
        part,
        startedStudyingAt,
      });
      // Only a card this call actually enrolled spends review recharge; an already-studied card
      // (a retry, or a character enrolled elsewhere) keeps its original introduction.
      if (
        meaningCard.startedStudyingAt === startedStudyingAt &&
        meaningCard.reviewRechargeAfter === null
      ) {
        await recordReviewRechargeIntroduction({
          actorDb: actorDb(import.meta.location),
          progressBefore: reviewRechargeBefore,
          reviewsDue: dueReviewCount > 0,
          userCardId: meaningCard.id,
        });
      }
      setHasLearnFlowStartedThisSession(true);
      setHasLearnedNewCharacterThisSession(true);
      return true;
    },
    mutationKey: ["createNewMorphemeLessonUserCard"],
    scope: { id: "lessonUserCardCreation" },
  });

  return useCallback(
    (grapheme: string, part?: GraphemeLessonPart, pinyin?: string): Promise<boolean> =>
      createUserCardWithProgressMutation.mutateAsync({ grapheme, part, pinyin }),
    [createUserCardWithProgressMutation],
  );
}
